import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Breadcrumbs from '../components/Breadcrumbs';
import { normalizeSeverity } from './Badge';

const STRIP = {
  crit: 'bg-crit',
  high: 'bg-high',
  med: 'bg-med',
  low: 'bg-low',
};

const isEmpty = (value) => value === null || value === undefined || value === '';

// items: [{ label, value, mono? }]. Empty values read "Not set".
export function Facts({ items, className = '' }) {
  return (
    <dl className={`flex flex-wrap gap-x-7 gap-y-2 ${className}`}>
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-small text-graphite">{item.label}</dt>
          <dd className={`font-semibold text-ink ${item.mono ? 'font-mono text-id' : ''}`}>
            {isEmpty(item.value) ? <span className="font-normal text-graphite">Not set</span> : item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// Clamped to three lines so a long description can't squeeze the tabs below;
// expanded text scrolls inside a capped height for the same reason.
function Summary({ text }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => setExpanded(false), [text]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return undefined;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <div className="mt-1 max-w-[72ch]">
      <p ref={ref} className={`text-section font-normal text-ink ${expanded ? 'max-h-[30vh] overflow-y-auto pr-2' : 'line-clamp-3'}`}>
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="mt-1 text-small text-signal hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

// Same footprint as a loaded header, so the tabs don't jump when data arrives.
export function EntityHeaderSkeleton({ crumbs, identifier }) {
  return (
    <header className="shrink-0 mb-5" aria-busy="true">
      {crumbs && <Breadcrumbs items={crumbs} className="mb-3" />}
      <div className="grid grid-cols-[6px_minmax(0,1fr)] gap-x-4">
        <div className="rounded-badge bg-rule" aria-hidden="true" />
        <div className="min-w-0">
          {identifier ? (
            <h1 className="font-mono text-entity tracking-[-0.01em] text-ink break-all">{identifier}</h1>
          ) : (
            <div className="h-[34px] flex items-center"><div className="h-5 w-72 rounded-badge bg-rule skeleton" /></div>
          )}
          <div className="mt-1 h-[22px] flex items-center"><div className="h-3 w-96 rounded-badge bg-rule skeleton" /></div>
          <div className="mt-3 flex gap-7" aria-hidden="true">
            {[64, 88, 56, 72, 80].map((w) => (
              <div key={w}>
                <div className="h-[18px] flex items-center"><div className="h-2.5 rounded-badge bg-rule skeleton" style={{ width: w - 16 }} /></div>
                <div className="h-5 flex items-center"><div className="h-3 rounded-badge bg-rule skeleton" style={{ width: w }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * Header for CVE, package and host pages. The identifier is the one large element;
 * the strip on its left carries the highest severity (a neutral rule when there is none).
 */
export default function EntityHeader({ crumbs, identifier, secondary, severity, summary, facts, actions, children }) {
  const tone = normalizeSeverity(severity)?.tone;

  return (
    <header className="shrink-0 mb-5">
      {crumbs && <Breadcrumbs items={crumbs} className="mb-3" />}
      <div className="grid grid-cols-[6px_minmax(0,1fr)_auto] gap-x-4">
        <div className={`rounded-badge ${STRIP[tone] || 'bg-rule'}`} aria-hidden="true" />
        <div className="min-w-0">
          <h1 className="font-mono text-entity tracking-[-0.01em] text-ink break-all">
            {identifier}
            {secondary && <span className="ml-2.5 font-normal text-graphite">{secondary}</span>}
          </h1>
          {summary && <Summary text={summary} />}
          {facts && <Facts items={facts} className="mt-3" />}
          {children}
        </div>
        {actions && <div className="flex items-start gap-2">{actions}</div>}
      </div>
    </header>
  );
}
